import { useState } from 'react';
import { Loader2, Receipt, ExternalLink, Ban, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { emitDte, consultNit, cancelDte } from '@/services/payments';
import { cn } from '@/lib/utils';
import type { Invoice } from '@asi/shared';

export interface AccountFelDefaults {
  fel_nit: string | null;
  fel_legal_name: string | null;
  fel_address: string | null;
  fel_email_invoice: string | null;
  billing_name: string | null;
  billing_email: string | null;
  billing_address: string | null;
  accountName: string;
}

interface Props {
  invoice: Invoice;
  accountId: string;
  accountFel: AccountFelDefaults;
  onEmitted: () => Promise<void> | void;
  /**
   * Optional optimistic callback fired immediately after a successful
   * cancellation. Parent can patch its local invoice state in place,
   * avoiding a full refetch (no flicker, no spinner). If omitted, parent
   * falls back to onEmitted() refresh.
   */
  onVoided?: (invoiceId: string) => void;
}

export function InvoiceFelActions({ invoice, accountId, accountFel, onEmitted, onVoided }: Props) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);
  const [nit, setNit] = useState(invoice.receptor_nit ?? accountFel.fel_nit ?? 'CF');
  const [name, setName] = useState(
    invoice.receptor_name ?? accountFel.fel_legal_name ?? accountFel.billing_name ?? accountFel.accountName ?? 'CONSUMIDOR FINAL',
  );
  const [address, setAddress] = useState(accountFel.fel_address ?? accountFel.billing_address ?? '');
  const [email, setEmail] = useState(
    invoice.receptor_email ?? accountFel.fel_email_invoice ?? accountFel.billing_email ?? '',
  );
  const [nitStatus, setNitStatus] = useState<null | { kind: 'ok' | 'err' | 'cf'; text: string; extras?: Array<{ label: string; value: string }> }>(null);
  const [validating, setValidating] = useState(false);

  /** Pluck any extra receptor fields INFILE returned (address, email,
   *  commercial name, regime, etc.) so we can show them for verification. */
  function extractReceptorExtras(raw?: Record<string, unknown>): Array<{ label: string; value: string }> {
    if (!raw) return [];
    const FIELDS: Array<[string, string[]]> = [
      ['Nombre comercial',   ['nombre_comercial', 'NOMBRE_COMERCIAL', 'nombreComercial']],
      ['Dirección',          ['direccion', 'DIRECCION']],
      ['Correo',             ['correo', 'CORREO', 'email', 'EMAIL']],
      ['Régimen',            ['regimen', 'REGIMEN', 'tipo_regimen']],
      ['Afiliación IVA',     ['afiliacion_iva', 'AFILIACION_IVA']],
      ['Estado',             ['estado', 'ESTADO']],
      ['Tipo contribuyente', ['tipo_contribuyente', 'TIPO_CONTRIBUYENTE']],
      ['Departamento',       ['departamento', 'DEPARTAMENTO']],
      ['Municipio',          ['municipio', 'MUNICIPIO']],
      ['Actualización',      ['fecha_actualizacion', 'FECHA_ACTUALIZACION']],
    ];
    const out: Array<{ label: string; value: string }> = [];
    for (const [label, keys] of FIELDS) {
      for (const k of keys) {
        const v = raw[k];
        if (typeof v === 'string' && v.trim()) { out.push({ label, value: v.trim() }); break; }
        if (typeof v === 'number') { out.push({ label, value: String(v) }); break; }
      }
    }
    return out;
  }

  async function handleValidateNit() {
    const clean = nit.trim().toUpperCase().replace(/[-\s]/g, '');
    if (!clean || clean === 'CF') {
      setNitStatus({ kind: 'cf', text: 'Consumidor Final' });
      setName('CONSUMIDOR FINAL');
      return;
    }
    setValidating(true);
    setNitStatus(null);
    try {
      const r = await consultNit(clean, accountId);
      if (r.valid && r.name) {
        setName(r.name);
        // Auto-fill address/email from INFILE response when our form is empty.
        const raw = r.raw ?? {};
        const dir = (raw.direccion ?? (raw as any).DIRECCION) as string | undefined;
        const cor = (raw.correo ?? (raw as any).CORREO) as string | undefined;
        if (dir && !address.trim()) setAddress(dir);
        if (cor && !email.trim())   setEmail(cor);
        setNitStatus({ kind: 'ok', text: `✓ ${r.name}`, extras: extractReceptorExtras(r.raw) });
      } else if (r.status === 'CREDENTIALS_NOT_REGISTERED') {
        setNitStatus({ kind: 'err', text: 'Consulta SAT no disponible en este ambiente. Podés emitir igual; INFILE rechazará si el NIT no existe.' });
      } else if (r.status === 'INFILE_ERROR' && r.message) {
        setNitStatus({ kind: 'err', text: `INFILE: ${r.message}` });
      } else {
        setNitStatus({ kind: 'err', text: `NIT no válido (${r.status})` });
      }
    } catch (e) {
      setNitStatus({ kind: 'err', text: e instanceof Error ? e.message : 'Error al consultar NIT' });
    } finally {
      setValidating(false);
    }
  }

  // Already certified → expose PDF link AND a small "Anular" button so admins
  // can cancel a wrongly-issued DTE without leaving the invoices grid.
  // If the invoice is already voided, only show the PDF link + an "Anulada" badge.
  if (invoice.fel_uuid && invoice.fel_pdf_url) {
    const felDocId = (invoice as unknown as { fel_document_id?: string | null }).fel_document_id;
    const isVoid   = invoice.status === 'void';

    // ── SAT cancellation-window badge ──────────────────────────────────────
    // Guatemala UTC-6 (no DST). Compute how many calendar days ago the DTE was certified.
    const toGtDateStr = (d: Date) => {
      const gt = new Date(d.getTime() - 6 * 3600 * 1000);
      return `${gt.getUTCFullYear()}-${String(gt.getUTCMonth() + 1).padStart(2, '0')}-${String(gt.getUTCDate()).padStart(2, '0')}`;
    };
    const felCertifiedAt = (invoice as unknown as { fel_certified_at?: string | null }).fel_certified_at ?? invoice.updated_at;
    const emissionDayGt  = felCertifiedAt ? toGtDateStr(new Date(felCertifiedAt)) : null;
    const todayGt        = toGtDateStr(new Date());
    const tomorrowOfEmission = emissionDayGt
      ? toGtDateStr(new Date(new Date(emissionDayGt + 'T06:00:00Z').getTime() + 24 * 3600 * 1000))
      : null;
    // "last chance" = DTE was emitted yesterday GT → must cancel today
    const isLastChance   = emissionDayGt && todayGt === tomorrowOfEmission;
    // "window expired" = DTE older than 1 calendar day GT → extemporaneous only
    const windowExpired  = emissionDayGt && todayGt !== emissionDayGt && todayGt !== tomorrowOfEmission;
    async function handleCancelSubmit() {
      const reason = cancelReason.trim();
      if (reason.length < 5) {
        setCancelError('El motivo debe tener al menos 5 caracteres.');
        return;
      }
      if (!felDocId) {
        setCancelError('No se encontró el ID del documento FEL.');
        return;
      }
      setCancelling(true);
      setCancelError(null);
      try {
        const res = await cancelDte({ felDocumentId: felDocId, reason });
        setCancelling(false);
        setCancelSuccess(`Anulada en SAT · UUID ${res.uuid?.slice(0, 8) ?? '—'}…`);
        // Optimistic update first so the row flips to "Anulada" instantly,
        // then close the modal. If the parent didn't supply onVoided, fall
        // back to onEmitted() once the success toast has been visible.
        if (onVoided) {
          onVoided(invoice.id);
          setTimeout(() => {
            setCancelOpen(false);
            setCancelSuccess(null);
            setCancelReason('');
          }, 1400);
        } else {
          setTimeout(() => {
            setCancelOpen(false);
            setCancelSuccess(null);
            setCancelReason('');
            void onEmitted();
          }, 1800);
        }
      } catch (e) {
        setCancelError(e instanceof Error ? e.message : String(e));
        setCancelling(false);
      }
    }
    return (
      <>
        <div className="inline-flex items-center gap-2 shrink-0">
          <a
            href={invoice.fel_pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="w-3 h-3" /> PDF
          </a>
          {isVoid ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-500 bg-rose-500/10 border border-rose-500/30 px-2 py-0.5 rounded-md">
              <Ban className="w-3 h-3" /> Anulada
            </span>
          ) : felDocId && (
            <div className="inline-flex items-center gap-1">
              {isLastChance && (
                <span title="Último día para anular sin proceso extemporáneo (regla SAT)" className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-md">
                  <Clock className="w-2.5 h-2.5" /> Último día
                </span>
              )}
              {windowExpired && (
                <span title="Plazo de anulación directa vencido. Requiere anulación extemporánea en portal SAT/INFILE." className="inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-500 bg-slate-500/10 border border-slate-400/30 px-1.5 py-0.5 rounded-md">
                  <Clock className="w-2.5 h-2.5" /> Plazo vencido
                </span>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-500/10"
                onClick={() => setCancelOpen(true)}
                title={windowExpired ? 'Plazo de anulación directa vencido — requiere proceso extemporáneo en SAT/INFILE' : 'Anular DTE en SAT'}
              >
                <Ban className="w-3.5 h-3.5 mr-1" /> Anular
              </Button>
            </div>
          )}
        </div>
        {cancelOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-2xl w-full max-w-md p-5 space-y-3">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Anular factura electrónica</h3>
                <p className="text-xs text-muted-foreground">
                  Factura {invoice.invoice_number} · DTE {invoice.fel_uuid}
                </p>
                {windowExpired && (
                  <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                    <strong>Plazo de anulación directa vencido.</strong> Esta factura fue emitida el {emissionDayGt}.
                    Según las reglas SAT, solo se puede anular el mismo día o el día siguiente.
                    Para anular esta factura contacta a INFILE al <strong>PBX 2458-2458 opción 2</strong> o <strong>sac6@infile.com</strong> para el proceso de anulación extemporánea.
                  </div>
                )}
                {isLastChance && (
                  <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                    <strong>Último día para anular.</strong> Esta factura fue emitida ayer — el plazo de anulación directa vence hoy a medianoche (hora Guatemala).
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-foreground">Motivo de anulación</label>
                <Input
                  value={cancelReason}
                  onChange={(e) => { setCancelReason(e.target.value); setCancelError(null); }}
                  placeholder="Ej. Datos del receptor incorrectos"
                  className="mt-1"
                  maxLength={250}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Mínimo 5 caracteres. SAT exige un motivo para cada anulación.
                </p>
              </div>
              {cancelSuccess && (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600">
                  ✓ {cancelSuccess}
                </div>
              )}
              {cancelError && (
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-600">
                  {cancelError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={cancelling}>
                  Cerrar
                </Button>
                <Button
                  onClick={handleCancelSubmit}
                  disabled={cancelling || !!cancelSuccess || cancelReason.trim().length < 5}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                  {cancelling
                    ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Anulando…</>
                    : cancelSuccess ? 'Anulada' : 'Confirmar anulación'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const eligible = ['paid', 'issued', 'overdue', 'draft'].includes(invoice.status);
  if (!eligible) return null;
  const isDraft = invoice.status === 'draft';
  const canCancelPlanInvoice = ['draft', 'issued', 'overdue'].includes(invoice.status)
    && ['plan_activation', 'patrol_dispatch'].includes((invoice as unknown as { invoice_type?: string | null }).invoice_type ?? '');

  async function handleCancel() {
    if (!confirm(`¿Cancelar la factura ${invoice.invoice_number}? El cliente no pagó y el plan no se activó — se anula la factura y la suscripción vuelve a quedar sin plan activo.`)) return;
    setSubmitting(true);
    setError(null);
    try {
      const { voidPendingPlanInvoice } = await import('@/services/billing');
      await voidPendingPlanInvoice(invoice.id);
      if (onVoided) onVoided(invoice.id);
      else await onEmitted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEmit() {
    setSubmitting(true);
    setError(null);
    try {
      const clean = nit.trim().toUpperCase().replace(/[-\s]/g, '');
      if (isDraft) {
        // Approve first (draft → issued) WITHOUT emitting FEL automatically,
        // then call emitDte with the receptor data the user typed in this modal.
        // This avoids the silent-failure UX where invoice-approve emitted FEL
        // using account defaults (ignoring the form) and returned fel_error
        // quietly, forcing a second click.
        const { approveInvoice } = await import('@/services/billing');
        await approveInvoice(invoice.id, false);
      }
      await emitDte({
        invoiceId: invoice.id,
        receptor: {
          nit:     clean || 'CF',
          name:    name.trim() || 'CONSUMIDOR FINAL',
          address: address.trim() || undefined,
          email:   email.trim()   || undefined,
        },
      });
      setOpen(false);
      await onEmitted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {canCancelPlanInvoice && (
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0 h-8 px-2 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-500/10"
          onClick={handleCancel}
          disabled={submitting}
          title="Cancelar factura no pagada (el plan nunca se activó)"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Ban className="w-3.5 h-3.5 mr-1" />}
          Cancelar
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        className="shrink-0 h-8 px-2 text-xs"
        onClick={() => setOpen(true)}
      >
        <Receipt className="w-3.5 h-3.5 mr-1" />
        {isDraft ? 'Aprobar y emitir' : 'Emitir FEL'}
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md p-5 space-y-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Emitir factura electrónica</h3>
              <p className="text-xs text-muted-foreground">Factura {invoice.invoice_number} · {invoice.currency} {invoice.amount.toFixed(2)}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-medium text-foreground">NIT del receptor</label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={nit}
                    onChange={(e) => { setNit(e.target.value); setNitStatus(null); }}
                    placeholder="CF"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleValidateNit}
                    disabled={validating}
                    className="shrink-0"
                  >
                    {validating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Validar'}
                  </Button>
                </div>
                {nitStatus && (
                  <div className={cn(
                    'text-[11px] mt-1 space-y-0.5',
                    nitStatus.kind === 'ok'  ? 'text-emerald-600 dark:text-emerald-400' :
                    nitStatus.kind === 'cf'  ? 'text-muted-foreground' :
                                               'text-rose-600 dark:text-rose-400',
                  )}>
                    <p>{nitStatus.text}</p>
                    {nitStatus.extras && nitStatus.extras.length > 0 && (
                      <ul className="text-muted-foreground space-y-0.5 mt-1 border-l-2 border-emerald-500/30 pl-2">
                        {nitStatus.extras.map((x) => (
                          <li key={x.label}>
                            <span className="font-medium">{x.label}:</span> {x.value}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-foreground">Nombre / Razón social</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="CONSUMIDOR FINAL" className="mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-foreground">Dirección</label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Ciudad" className="mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-foreground">Correo (opcional)</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@dominio.com" className="mt-1" />
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-600">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>Cancelar</Button>
              <Button onClick={handleEmit} disabled={submitting}>
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Emitiendo…</> : 'Emitir DTE'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
